import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useApp }              from '../../../app/AppContext.jsx'
import { useCatalog }          from '../../catalog/context/CatalogContext.jsx'

import { HERO_VIEW }           from '../../../shared/constants/heroView.js'
import { HERO_OUTLINE }        from '../../../shared/constants/heroOutline.js'

import { useHeroCapabilities } from '../hooks/useHeroCapabilities.js'
import { 
  resolveHeroBuildingId, 
  resolveHeroView 
} from '../utils/heroUtils.js'

// ── Context ────────────────────────────────────────────────────────────────────

const HeroContext = createContext(null)

// ── Provider ───────────────────────────────────────────────────────────────────

/**
 * Domain-level context provider for the Hero section.
 *
 * Owns hero-specific state:
 *   - `activeView`    — which sub-view is shown (visualization / site / building)
 *   - `activeOutline` — which SVG outline overlay is active
 *   - `isOutlineVisible` — whether the overlay is toggled on
 *
 * Cross-domain state (`activeBuilding`, `activeTier`) stays in AppContext.
 * This provider reads `projectConfig` and `panelSettings` from AppContext
 * to derive capabilities, but does not fetch data itself.
 *
 * Must be mounted inside `AppContextProvider`.
 * Should wrap Hero domain components — not placed in `AppContextProvider`.
 */
export function HeroContextProvider({ children }) {
  const {
    projectConfig,
    panelSettings,
    activeBuilding,
    activeHeroView, //move here to 'presentation:{}'? from app? no... its a navigation state ...
    setActiveBuilding,
    changeHeroViewHandler,
  } = useApp()
  const { buildings } = useCatalog()

  // ── Capabilities (derived, read-only) ─────────────────────────────────────

  const heroCapabilities = useHeroCapabilities(projectConfig, panelSettings)

  // ── Active view ───────────────────────────────────────────────────────────

  const activeView = activeHeroView
  const [activeOutline, setActiveOutline] = useState(() => heroCapabilities.initialOutline)
  const [isOutlineVisible, setIsOutlineVisible] = useState(true)

  useEffect(() => {
    const resolvedCurrentView = resolveHeroView(activeView, activeView, heroCapabilities)
    if (resolvedCurrentView !== activeView) {
      changeHeroViewHandler(resolvedCurrentView)
    }
  }, [activeView, changeHeroViewHandler, heroCapabilities])

  // ── changeViewHandler ─────────────────────────────────────────────────────
  // Composite action: changes view and adjusts activeOutline as needed.
  //
  // Rules:
  //   → BUILDING      always switches to APARTMENTS (show units immediately)
  //   → VISUALIZATION keeps current activeOutline (main.svg has its own outlines)
  //   → SITE          keeps current if still available; falls back to first in siteOutlineLayers

  const changeViewHandler = useCallback((newView, { buildingId = null } = {}) => {
    const resolvedView = resolveHeroView(newView, activeView, heroCapabilities)
    const resolvedBuildingId = resolveHeroBuildingId(resolvedView, activeBuilding, buildings, buildingId)

    changeHeroViewHandler(resolvedView)

    if (resolvedView === HERO_VIEW.BUILDING) {
      if (resolvedBuildingId) {
        setActiveBuilding(resolvedBuildingId)
      }
      setActiveOutline(HERO_OUTLINE.APARTMENTS)
    } else if (resolvedView === HERO_VIEW.SITE) {
      setActiveOutline((prev) => {
        const available = heroCapabilities.siteOutlineLayers
        return available.includes(prev) ? prev : (available[0] ?? HERO_OUTLINE.BUILDINGS)
      })
    }
    // VISUALIZATION: no change to activeOutline
  }, [
    activeBuilding,
    activeView,
    buildings,
    changeHeroViewHandler,
    heroCapabilities,
    setActiveBuilding,
  ])

  // ── Available outlines for current view ───────────────────────────────────
  // Guard: only outlines enabled in config for the current view are exposed.

  const availableOutlines = useMemo(() => {
    switch (activeView) {
      case HERO_VIEW.VISUALIZATION: return heroCapabilities.visOutlineLayers
      case HERO_VIEW.SITE:          return heroCapabilities.siteOutlineLayers
      case HERO_VIEW.BUILDING:      return heroCapabilities.buildingOutlineLayers
      default:                      return []
    }
  }, [activeView, heroCapabilities])

  // ── Context value ──────────────────────────────────────────────────────────

  const value = {
    // Capabilities (warstwa 1 — read-only)
    heroCapabilities,
    // Active view (warstwa 2)
    activeView,
    changeViewHandler,
    // Active outline (warstwa 4)
    activeOutline,
    setActiveOutline,
    isOutlineVisible,
    setIsOutlineVisible,
    // Derived
    availableOutlines,
  }

  return (
    <HeroContext.Provider value={value}>
      {children}
    </HeroContext.Provider>
  )
}

// ── Consumer hook ──────────────────────────────────────────────────────────────

/**
 * Hook to consume the HeroContext.
 * Must be used within `HeroContextProvider`.
 *
 * @returns {object} HeroContext value
 */
export function useHero() {
  const ctx = useContext(HeroContext)
  if (!ctx) throw new Error('useHero must be used within HeroContextProvider')
  return ctx
}
